import * as React from "react";
import {CardBoxSmall} from "../../../components/CardBox";
import {Title} from "../../../components/Title";
import {Legend, LegendLiElement} from "../../../components/charts/Legend";
import {ColorUtils} from "../../../components/Utils/color/ColorUtils";
import {Loader} from "../../../components/Utils/loader/Loader";
import {StatisticsUtils} from "../../../components/Utils/statistics/StatisticsUtils";
import {AppTranslations} from "../../../components/Utils/translations/AppTranslations";
import {AlarmSeverityGuid} from "../../alarms/data/AlarmsMonitorData";
import {AlarmsOpenedTexts, CategoriesColors, DashboardContainer, WidgetResponse} from "../../DashboardContainer";
import {JSONMapper} from "../../data/JSONMapper";
import {PieChartWithLabels} from "../charts/PieChartWithLabels";
import {FormatLocaleNumber} from "../DashboardUtils";
import {ZERO_GUID} from "./EquipmentByLevelWidget";
import {LegendTooltip, LegendTooltipConfig} from "./LegendTooltip";
import {REGISTERED_LOCATIONS_IDS, SiteTypes} from "./MarketWidget";
import {OpenAlarmsByLevelWidget} from "./OpenAlarmsByLevelWidget";
import {RestDataLoader} from "./RestDataLoader";

import "./style/OpenAlarmsWidget.scss";
import {LegacyBadge} from "../../../components/LegacyBadge";
import {VeBoxCard} from "../../../components/card/VeBoxCard";
import {WidgetBadge, WidgetBadgeType} from "../DashboardWidget";
import AutoSizer from "react-virtualized-auto-sizer";

declare var AppTranslations: AppTranslations;

interface OpenAlarmsWidgetProps {
    data: WidgetResponse;
    texts: AlarmsOpenedTexts;
    colorGenerator: ColorUtils;
}

interface WidgetState {
    ready: boolean;
    error: boolean;
    equipmentWithAlarmsNumber: number;
    equipmentNumber: number;
    alarmsByType: AlarmByType[];
    alarmsByEquipmentFamilyAndType: AlarmByEquipmentType[];
    selectedItemName: string;
    tooltipConfig: LegendTooltipConfig;
}

export interface AlarmByType {
    typeGuid: string;
    typeName: string;
    typeColor?: string;
    alarmsCount: number;
    severity?: string;
}

export interface AlarmByEquipmentType {
    familyName: string;
    familyIcon: string;
    familyGuid?: string;
    familyGuids?: string[];
    alarmsCount: number;
    equipmentWithAlarm: number;
    totalEquipment: number;
    alarmOpenAgeAvg: number;
    alarmsByType: AlarmByType[];
}

enum AlarmStatus {
    PENDING = 0,
    MANAGED = 1,
}

export class OpenAlarmsWidget extends React.Component<OpenAlarmsWidgetProps, WidgetState> {

    /**
     * Given a maximum number of families to show and the source set of data, group in a single family
     * called 'Others' all exceed families.
     *
     * @param data the data about alarms for each family
     * @param maxNumberOfFamilies the maximum number of family that can be shown (other included)
     * @param texts text translations
     */
    public static prepareData(
        data: AlarmByEquipmentType[],
        maxNumberOfFamilies: number,
        texts: any,
    ): AlarmByEquipmentType[] {
        let preparedData: any[];
        if (data.length <= maxNumberOfFamilies) {
            return data;
        }

        data = data.sort((a, b) => b.alarmsCount - a.alarmsCount);
        preparedData = data.slice(0, maxNumberOfFamilies - 1);
        const familiesToGroup = data.slice(maxNumberOfFamilies - 1, data.length);

        // group alarms types of the others families
        const otherFamiliesAlarmsByType: Map<string, AlarmByType> = new Map<string, AlarmByType>();
        familiesToGroup.forEach((family) => {
            family.alarmsByType.forEach((at) => {
                const otherAlarmType = otherFamiliesAlarmsByType.get(at.typeGuid);
                if (!otherAlarmType) {
                    otherFamiliesAlarmsByType.set(at.typeGuid, {...at});
                } else {
                    otherFamiliesAlarmsByType.set(at.typeGuid, {
                        ...otherAlarmType,
                        alarmsCount: otherAlarmType.alarmsCount + at.alarmsCount,
                    });
                }
            });
        });
        // group other families
        const otherFamilies = {
            familyName: texts.others,
            familyIcon: null,
            familyGuids: familiesToGroup.map((x) => x.familyGuid),
            alarmsCount: familiesToGroup.reduce((a, b) => a + b.alarmsCount, 0),
            equipmentNumber: familiesToGroup.reduce((a, b) => a + b.equipmentWithAlarm, 0),
            totalEquipment: familiesToGroup.reduce((a, b) => a + b.totalEquipment, 0),
            equipmentWithAlarm: familiesToGroup.reduce((a, b) => a + b.equipmentWithAlarm, 0),
            alarmsByType: Array.from(otherFamiliesAlarmsByType.values()),
        };
        preparedData.push(otherFamilies);
        return preparedData;
    }

    private alarmByTypeByName: Map<string, AlarmByType>;

    constructor(props: OpenAlarmsWidgetProps) {
        super(props);
        this.state = {
            ready: false,
            error: false,
            equipmentWithAlarmsNumber: null,
            equipmentNumber: null,
            alarmsByType: null,
            alarmsByEquipmentFamilyAndType: null,
            selectedItemName: null,
            tooltipConfig: {visible: false},
        };
        this.alarmByTypeByName = new Map();
        this.setSelectedItem = this.setSelectedItem.bind(this);
    }

    public setSelectedItem(itemName: string, e): void {
        const target: HTMLLIElement = e ? e.currentTarget : null;
        const alarm = this.alarmByTypeByName.get(itemName);

        this.setState({
            selectedItemName: itemName,
        }, () => {
            if (itemName && (alarm === undefined)) {
                const otherAlarmTypes = OpenAlarmsByLevelWidget
                    .getAlarmByType(this.state.alarmsByEquipmentFamilyAndType)
                    .filter((x, i) => i >= 9);

                if (target) {
                    this.setState({
                        tooltipConfig: {
                            visible: true,
                            offSetX: target.offsetLeft,
                            offSetY: target.offsetTop + target.offsetHeight,
                            width: target.offsetWidth,
                            data: otherAlarmTypes,
                            father: target,
                        },
                    });
                }
            }
        });
    }

    public ready() {
        if (this.state.equipmentNumber != null
            && this.state.equipmentWithAlarmsNumber != null
            && this.state.alarmsByType != null
            && this.state.alarmsByEquipmentFamilyAndType != null
        ) {
            this.setState({ready: true});
        }
    }

    public componentDidMount() {
        RestDataLoader.loadData(
            "statistics/alarms/counting/alarmType",
            {
                statuses: "pending,managed",
                withTelemetry: true,
                placements: "warehouse,market",
            },
            (data: any) => {
                this.setState({
                    alarmsByType: JSONMapper.jsonAlarmsByTypeMapper(data.list),
                }, () => {
                    this.state.alarmsByType.forEach((alarm) => {
                        this.alarmByTypeByName.set(alarm.typeName, alarm);
                    });
                });
                this.ready();
            },
            () => {
                this.setState({error: true});
            },
        );

        RestDataLoader.loadData(
            "statistics/alarms/counting/equipmentTypeAndAlarmType",
            {
                statuses: "pending,managed",
                withTelemetry: true,
                placements: "warehouse,market",
            },
            (data: any) => {
                const result: AlarmByEquipmentType[] = JSONMapper.jsonAlarmsCountByEquipmentFamilyMapper(data.list);
                let equipmentNumber: number = 0;
                let equipmentWithAlarmsNumber: number = 0;
                result.forEach((element) => {
                    equipmentNumber += element.totalEquipment;
                    equipmentWithAlarmsNumber += element.equipmentWithAlarm;
                });
                this.setState(
                    {
                        equipmentNumber,
                        equipmentWithAlarmsNumber,
                        alarmsByEquipmentFamilyAndType: result,
                    }, () => {
                        OpenAlarmsByLevelWidget
                            .getAlarmByType(this.state.alarmsByEquipmentFamilyAndType)
                            .forEach((alarm) => {
                                this.alarmByTypeByName.set(alarm.typeName, alarm);
                            });
                    },
                );

                this.ready();
            },
            () => {
                this.setState({error: true});
            },
        );
    }

    public render() {
        if (this.state.error) {
            return null;
        }
        let content = <Loader/>;
        if (!this.state.ready) {
            return (
                <div className="open-alarms-widget">
                    {content}
                </div>
            );
        }
        const alarmsByType = this.state.alarmsByType;
        const dataSet = alarmsByType.map(
            (item, i) => {
                return {
                    id: `${i}`,
                    code: item.typeGuid,
                    label: item.typeName,
                    frequency: item.alarmsCount,
                    color: item.typeColor
                        || this.props.colorGenerator.getMappedColor(CategoriesColors.ALARM_TYPES, item.typeGuid),
                };
            });

        // grouping exceeding alarm types into other type
        const alarmsByTypeReduced = PieChartWithLabels.prepareData(
            dataSet,
            10,
            "desc",
            {others: this.props.texts.others},
            undefined,
        );

        const total = alarmsByTypeReduced.reduce<number>((a, x) => a + x.frequency, 0);

        const legendItems = alarmsByTypeReduced.map((pd) => {
            const color = this.alarmByTypeByName.has(pd.label)
                ? OpenAlarmsByLevelWidget
                    .getColorBasedOnSeverity(this.alarmByTypeByName.get(pd.label).severity)
                : undefined;
            const text = this.alarmByTypeByName.has(pd.label)
                ? OpenAlarmsByLevelWidget
                    .getTextBasedOnSeverity(this.alarmByTypeByName.get(pd.label).severity)
                : undefined;
            return {
                color: pd.color,
                name: pd.label,
                value: pd.frequency,
                percentage: StatisticsUtils.getPercentage(total, pd.frequency),
                icon: pd.icon,
                afterName: (
                    <div
                        style={{
                            display: "inline",
                            width: 65,
                        }}
                    >
                        <LegacyBadge
                            innerText={text}
                            color={text && this.alarmByTypeByName
                                .get(pd.label).severity === AlarmSeverityGuid.WARNING
                                ? "#000"
                                : "#FFF"}
                            backgroundColor={color}
                            width={42}
                        />
                    </div>
                ),
            };
        });

        const reducedAlarmType = dataSet.filter(
            (original) =>
                alarmsByTypeReduced.filter((target) => target.label === original.label).length === 0,
        );

        const familiesData = OpenAlarmsWidget.prepareData(
            this.state.alarmsByEquipmentFamilyAndType,
            4,
            {others: this.props.texts.others},
        );

        const title = this.props.texts.title;
        const familyCards = familiesData.map((element, index) => {
                const data = element.alarmsByType;
                const dataSetByComponentType = data.map(
                    (item, i) => {
                        return {
                            id: `${i}`,
                            code: item.typeGuid,
                            label: item.typeName,
                            frequency: item.alarmsCount,
                            color: (item.typeColor
                                || this.props.colorGenerator.getMappedColor(CategoriesColors.ALARM_TYPES, item.typeGuid)
                            ),
                        };
                    },
                );
                const preparedData = PieChartWithLabels.prepareData(
                    dataSetByComponentType,
                    Infinity,
                    "desc",
                    {others: this.props.texts.others},
                    reducedAlarmType,
                );

                const footerKey = DashboardContainer.codifyEntityName(
                    "chartFooterComponentyType-" + element.familyName,
                );
                const pieChartFooter = (
                    <div className="pie-chart__footer" key={footerKey}>
                        <div className="pie-chart__footer__row">
                            <span className="pie-chart__footer__row__label">
                                {this.props.texts.equipmentWithAlarms}:
                            </span>
                            <span className="pie-chart__footer__row__value">
                                {FormatLocaleNumber(element.equipmentWithAlarm)}
                                {" "}
                                {this.props.texts.on}
                                {" "}
                                {FormatLocaleNumber(element.totalEquipment)}
                                {" ("}
                                {FormatLocaleNumber(StatisticsUtils.getPercentage(
                                    element.totalEquipment,
                                    element.equipmentWithAlarm), {toFixed: 1})
                                }%{")"}
                            </span>
                        </div>
                        <div className="pie-chart__footer__row">
                            <span className="pie-chart__footer__row__label">
                                {this.props.texts.averageAlarmsByEquipment}:
                            </span>
                            <span className="pie-chart__footer__row__value">
                                {FormatLocaleNumber(element.alarmsCount / element.totalEquipment, {toFixed: 1})}
                            </span>
                        </div>
                        <div className="pie-chart__footer__row">
                            <span className="pie-chart__footer__row__label">
                                {this.props.texts.openedAlarmsAverageAge}:
                            </span>
                            <span className="pie-chart__footer__row__value">
                                {element.alarmsCount === 0 ? "-" :
                                    StatisticsUtils.periodInWords(
                                        element.alarmOpenAgeAvg,
                                        this.props.texts.timeTexts,
                                        2,
                                    )
                                }
                            </span>
                        </div>
                    </div>
                );

                const chartKey = DashboardContainer.codifyEntityName("chartComponentType-" + element.familyName);
                return (
                    <div className="column" key={chartKey}>
                        <AutoSizer style={{width: "auto"}}>
                            {({width, height}) =>
                                <div className="pie-chart__graph">
                                    <div className="pie-chart__title">
                                        {element.familyIcon && <img
                                            className={"pie-chart__title__icon"}
                                            src={element.familyIcon}
                                        />}
                                        <span className={"pie-chart__title__text"}>{element.familyName}</span>
                                    </div>
                                    <PieChartWithLabels
                                        donutOptions={{activeDonut: true, showTotal: true}}
                                        width={width}
                                        height={Math.min(height / 2, 95)}
                                        radiusRatio={2}
                                        data={preparedData}
                                        showPercentage={false}
                                        showIcon={false}
                                        noDataMessage={this.props.texts.noData}
                                        iconSize={{width: 40, height: 40}}
                                        labelLineDefaultLength={10}
                                        order={"desc"}
                                        selectedItem={this.state.selectedItemName}
                                        labelsOptions={{externalLegend: true}}
                                        onPathOver={this.setSelectedItem}
                                        onClick={
                                            element.familyGuid
                                                ? this.onAlarmByFamilyChartClick(element.familyGuid)
                                                : this.onAlarmByFamilyChartClick(...element.familyGuids)
                                        }
                                    />
                                    {pieChartFooter}
                                </div>}
                        </AutoSizer>
                    </div>
                );
            },
        );

        const onOtherTooltipClicked = (alarmName) => () => this.onItemClick(alarmName);
        const onOtherTooltipHover = (_, e) => null;

        content = (
            <VeBoxCard className="open-alarms-widget__container home-dashboard-card">
                <VeBoxCard.Header
                    className="open-alarms-widget__header technical-header space-between"
                    title={title}
                    titleClassName={"lowercase capitalize"}
                    singleLineTitle={true}
                    back={<WidgetBadge type={WidgetBadgeType.COMMERCIAL}/>}
                />
                <VeBoxCard.Body className="open-alarms-widget__content" style={{padding: "10px 20px"}}>
                    <div className="row">
                        <div className="cards-container">
                            <div className="top-card-container">
                                <CardBoxSmall
                                    title={this.props.texts.equipmentWithAlarms}
                                    value={
                                        FormatLocaleNumber(this.state.equipmentWithAlarmsNumber)
                                        + " "
                                        + this.props.texts.on
                                        + " "
                                        + FormatLocaleNumber(this.state.equipmentNumber)
                                        + " ("
                                        + FormatLocaleNumber(StatisticsUtils.getPercentage(
                                            this.state.equipmentNumber,
                                            this.state.equipmentWithAlarmsNumber), {toFixed: 1})
                                        + "%)"
                                    }
                                    bolder={false}
                                    extended={false}
                                    onClick={this.onOpenAlarms}
                                />

                                <CardBoxSmall
                                    title={this.props.texts.averageAlarmsByEquipment}
                                    value={
                                        FormatLocaleNumber(
                                            this.state.alarmsByType.reduce((a, b) =>
                                                a + b.alarmsCount, 0) / this.state.equipmentNumber,
                                            {toFixed: 1})
                                    }
                                    bolder={false}
                                    extended={false}
                                />
                            </div>
                        </div>
                        <div className="chart-container">
                            <div className="pie-chart__graph">
                                <AutoSizer style={{width: "auto"}}>
                                    {({width, height}) => <PieChartWithLabels
                                        donutOptions={{activeDonut: true, showTotal: true}}
                                        width={width - 40}
                                        height={height}
                                        radiusRatio={2}
                                        data={alarmsByTypeReduced}
                                        showPercentage={true}
                                        showIcon={false}
                                        noDataMessage={this.props.texts.noData}
                                        labelLineDefaultLength={15}
                                        order={"desc"}
                                        selectedItem={this.state.selectedItemName}
                                        labelsOptions={{externalLegend: true}}
                                        onPathOver={this.setSelectedItem}
                                        onClick={this.onMainChartClick}
                                    />}
                                </AutoSizer>
                            </div>
                            <div className="pie-chart__legend__container">
                                <Legend
                                    items={legendItems}
                                    selectedItemName={this.state.selectedItemName}
                                    onItemHover={this.setSelectedItem}
                                    singleColumn={true}
                                    showValues={true}
                                    onClick={this.onItemClick}
                                    capitalize={true}
                                />
                                <LegendTooltip
                                    config={this.state.tooltipConfig}
                                    className={"tooltip-wrapper"}
                                    style={{
                                        color: "white",
                                    }}
                                >
                                    {(alarms: AlarmByType[]) => (
                                        <div
                                            className={"tooltip-body"}
                                            style={{
                                                maxHeight: 220,
                                                overflow: "auto",
                                            }}
                                        >
                                            <ul className="legend">
                                                {alarms.map((alarm, i) => {
                                                    const color = this.alarmByTypeByName.has(alarm.typeName)
                                                        ? OpenAlarmsByLevelWidget
                                                            .getColorBasedOnSeverity(this.alarmByTypeByName
                                                                .get(alarm.typeName).severity)
                                                        : undefined;
                                                    const text = this.alarmByTypeByName.has(alarm.typeName)
                                                        ? OpenAlarmsByLevelWidget
                                                            .getTextBasedOnSeverity(this.alarmByTypeByName
                                                                .get(alarm.typeName).severity)
                                                        : undefined;
                                                    return (
                                                        <LegendLiElement
                                                            key={i}
                                                            capitalize={true}
                                                            item={{
                                                                name: alarm.typeName,
                                                                color: legendItems[legendItems.length - 1].color,
                                                                value: alarm.alarmsCount,
                                                                code: alarm.typeGuid,
                                                                percentage: StatisticsUtils.getPercentage(
                                                                    total,
                                                                    alarm.alarmsCount,
                                                                ),
                                                                afterName: (
                                                                    <div
                                                                        style={{
                                                                            display: "inline",
                                                                            width: 65,
                                                                        }}
                                                                    >
                                                                        <LegacyBadge
                                                                            innerText={text}
                                                                            color={text && this.alarmByTypeByName
                                                                                .get(alarm.typeName).severity ===
                                                                            AlarmSeverityGuid.WARNING
                                                                                ? "#000"
                                                                                : "#FFF"}
                                                                            backgroundColor={color}
                                                                            width={42}
                                                                        />
                                                                    </div>
                                                                ),
                                                            }}
                                                            iconOptions={{show: false}}
                                                            selectionKey={"code"}
                                                            onClick={onOtherTooltipClicked(alarm.typeName)}
                                                            showValues={true}
                                                            liStyle={{
                                                                color: "white",
                                                            }}
                                                            onHover={onOtherTooltipHover}
                                                        />
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    )}
                                </LegendTooltip>
                            </div>
                        </div>
                    </div>
                    <Title title={this.props.texts.alarmsByType}/>
                    <div className="row-inline">
                        {familyCards}
                    </div>
                </VeBoxCard.Body>
            </VeBoxCard>
        );
        return (
            <div className="open-alarms-widget">
                {content}
            </div>
        );
    }

    private onItemClick = (d) => {
        const alarm: AlarmByType = this.alarmByTypeByName.get(d);
        if (alarm !== undefined) {
            (window as any).GlobalStackManager.addNewLevel("EquipmentListLevel", {
                alarmStatuses: [AlarmStatus.PENDING, AlarmStatus.MANAGED],
                withoutAlarmTypes: false,
                alarmTypeGuids: [alarm.typeGuid],
                sites: [
                    ...REGISTERED_LOCATIONS_IDS,
                    SiteTypes.UNREGISTERED_SITE,
                    SiteTypes.WAREHOUSE,
                ],
                allTelemetries: true,
                applyFilter: true,
            });
        } else {

            const otherAlarmTypesGuid = OpenAlarmsByLevelWidget
                .getAlarmByType(this.state.alarmsByEquipmentFamilyAndType)
                .filter((x, i) => i >= 9)
                .map((x) => x.typeGuid);

            (window as any).GlobalStackManager.addNewLevel("EquipmentListLevel", {
                alarmStatuses: [AlarmStatus.PENDING, AlarmStatus.MANAGED],
                withoutAlarmTypes: false,
                alarmTypeGuids: otherAlarmTypesGuid,
                sites: [
                    ...REGISTERED_LOCATIONS_IDS,
                    SiteTypes.UNREGISTERED_SITE,
                    SiteTypes.WAREHOUSE,
                ],
                allTelemetries: true,
                applyFilter: true,
            });
        }
    }

    private onMainChartClick = (d) => {
        (window as any).GlobalStackManager.addNewLevel("EquipmentListLevel", {
            alarmTypeGuids: [d.data.code],
            alarmStatuses: [AlarmStatus.PENDING, AlarmStatus.MANAGED],
            withoutAlarmTypes: false,
            sites: [
                ...REGISTERED_LOCATIONS_IDS,
                SiteTypes.UNREGISTERED_SITE,
                SiteTypes.WAREHOUSE,
            ],
            allTelemetries: true,
            applyFilter: true,
        });
    }

    private onAlarmByFamilyChartClick = (...familyGuids: string[]) => {
        return (d) => {
            if (familyGuids.indexOf(ZERO_GUID) === -1 && d.data.code !== ZERO_GUID) {
                (window as any).GlobalStackManager.addNewLevel("EquipmentListLevel", {
                    equipmentType: familyGuids,
                    alarmTypeGuids: [d.data.code],
                    alarmStatuses: [AlarmStatus.PENDING, AlarmStatus.MANAGED],
                    withoutAlarmTypes: false,
                    sites: [
                        ...REGISTERED_LOCATIONS_IDS,
                        SiteTypes.UNREGISTERED_SITE,
                        SiteTypes.WAREHOUSE,
                    ],
                    allTelemetries: true,
                    applyFilter: true,
                });
            }
        };
    }

    private onOpenAlarms = () => {
        (window as any).GlobalStackManager.addNewLevel("EquipmentListLevel", {
            alarmStatuses: [AlarmStatus.PENDING, AlarmStatus.MANAGED],
            withoutAlarmTypes: false,
            sites: [
                ...REGISTERED_LOCATIONS_IDS,
                SiteTypes.UNREGISTERED_SITE,
                SiteTypes.WAREHOUSE,
            ],
            allTelemetries: true,
            applyFilter: true,
        });
    }
}
